import { useMemo } from 'react';

import { getCoreRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { FileHistoryLog, LogEvent } from '../components/file-history-log';
import { useGetDeletedFiles } from '@/api/queries/useGetDeletedFiles';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { DeletionHistory, UploadHistory } from '@/api/types/backend-dto-types';

function buildLogEvents(uploads: UploadHistory[] | undefined, deletions: DeletionHistory[] | undefined): LogEvent[] {
  const events: LogEvent[] = [];

  // Add upload events
  uploads?.forEach((upload) => {
    const attempted = (upload.successfullyUploadedFileCount ?? 0) + (upload.uploadFailures?.length ?? 0);
    events.push({
      id: upload.id,
      type: 'upload',
      date: upload.createdAt as unknown as string, // Date type in entity, string from JSON
      userEmail: upload.createdBy?.email ?? 'Unknown',
      attempted,
      successful: upload.successfullyUploadedFileCount ?? 0,
      skipped: upload.uploadFailures?.length ?? 0,
    });
  });

  // Add deletion events
  deletions?.forEach((deletion) => {
    events.push({
      id: deletion.id,
      type: 'deletion',
      date: deletion.createdAt,
      userEmail: deletion.createdBy?.email ?? 'Unknown',
      count: deletion.fileDeletion?.length ?? 0,
      deletionType: deletion.type,
    });
  });

  // Sort by date descending (newest first)
  events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return events;
}

export function FileLog() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { data: uploads, isLoading } = useGetMatterUploadHistory(matterId);
  const { data: deletions, isLoading: isLoadingDeletions } = useGetDeletedFiles(matterId);

  const logEvents = useMemo(() => buildLogEvents(uploads, deletions), [uploads, deletions]);
  const isLogLoading = isLoading || isLoadingDeletions;

  const logTable = useReactTable({
    data: logEvents,
    columns: [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return <FileHistoryLog matterId={matterId} isLogLoading={isLogLoading} logEvents={logEvents} logTable={logTable} />;
}
